import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { PLAYBACK_SPEEDS } from '../../animation/clock';
import { MOTION_PRESETS, MOTION_PRESET_IDS, type MotionPresetId } from '../../animation/motionPresets';
import { durationSeconds } from '../../animation/timeline';
import * as A from '../../domain/animationOps';
import * as S from '../../domain/sceneOps';
import { ANIMATION_FPS, EasingSchema, POSE_CHANNELS, TRANSFORM_CHANNELS, type Animation, type AnimationTrack, type Channel, type Easing, type LoopMode, type Project, type Scene } from '../../domain/schemas';
import { createId } from '../../lib/ids';
import type { AnimationSnapshot } from '../../animation/animationController';
import type { Observable } from '../../lib/store';
import { useObservable, useObservableSelector } from '../../lib/useObservable';
import { useServices } from '../../state/services';
import { Button, ConfirmDialog, Panel } from '../ui';
import type { EditFn } from './useSceneEdit';

const sel = 'rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm';
const num = 'w-20 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1 text-sm';
const LABEL_W = 140;
/** Left padding inside lanes so keyframes on frame 0 are fully visible. */
const PAD = 10;
const EASING_LABELS: Record<Easing, string> = { step: 'Step (hold)', linear: 'Linear', 'ease-in': 'Ease in', 'ease-out': 'Ease out', 'ease-in-out': 'Ease in-out' };
const LOOP_LABELS: Record<LoopMode, string> = { once: 'Play once', loop: 'Loop', pingpong: 'Ping-pong' };

type KeyRef = A.KeyRef;

/** Generated keys a Motion Composer preview would add (shown distinct from existing keys). */
export interface PlannedOverlay {
  tracks: { objectId: string; channel: Channel; frames: number[] }[];
  /** `${objectId}/${channel}@${frame}` of existing keys inside generated ranges (conflicts). */
  conflicts: ReadonlySet<string>;
  /** Last frame the plan writes. */
  endFrame: number;
}
const refKey = (r: KeyRef) => `${r.trackId}@${r.frame}`;

/**
 * The animation timeline (Phase 3B): animation management, timing, playback toolbar with
 * Auto-Key, a zoomable track view grouped by object with draggable keyframes, a numeric
 * keyframe editor and motion presets. Every action has a keyboard or button alternative:
 * keyframes are focusable buttons (←/→ move one frame, Shift ×10, Delete removes), the
 * playhead follows `,` `.` (frame step), Home/End and Space (play/pause) while the track
 * area has focus.
 */
export function TimelinePanel({
  project,
  scene,
  animation,
  edit,
  frames,
  planned = null,
}: {
  project: Project;
  scene: Scene;
  animation: Animation | null;
  edit: EditFn;
  frames: Observable<AnimationSnapshot>;
  planned?: PlannedOverlay | null;
}) {
  const { animation: bus, selection } = useServices();
  // Per-frame values are read by small child components (Playhead*, KeyNav) or at click
  // time (`frameNow`), so playback and scrubbing do not re-render the track area.
  const playing = useObservableSelector(bus.clock, (c) => c.playing);
  const speed = useObservableSelector(bus.clock, (c) => c.speed);
  const autoKey = useObservable(bus.autoKey);
  const frameNow = () => bus.clock.frame();
  const selected = useObservable(selection);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [shrink, setShrink] = useState<{ seconds: number; count: number } | null>(null);
  const [zoom, setZoom] = useState(8);
  const [selectedKeys, setKeys] = useState<KeyRef[]>([]);
  const [drag, setDrag] = useState<{ pointerId: number; startX: number; delta: number; moved: boolean } | null>(null);
  const scrubbing = useRef<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const tracksRef = useRef<HTMLDivElement>(null);
  const animations = project.animations.filter((a) => a.sceneId === scene.id);
  const a = animation;

  // Keyframe selection belongs to the current animation; refs that no longer exist (after
  // undo, deletion or switching animation) are simply not selected.
  const keys = a ? selectedKeys.filter((r) => a.tracks.some((t) => t.id === r.trackId && t.keyframes.some((k) => k.frame === r.frame))) : [];

  const run = (label: string, op: (p: Project) => Project, key?: string) => {
    const ok = edit(label, op, key);
    setMessage(null);
    return ok;
  };

  const create = () => {
    let id = '';
    if (
      run('New animation', (p) => {
        const r = A.createAnimation(p, scene.id, {}, createId('anm'));
        id = r.id;
        return r.project;
      })
    ) {
      selection.selectAnimation(id);
      bus.clock.seek(0);
    }
  };

  // ---------------------------------------------------------------- groups and rows
  const selectedObjects = scene.objects.filter((o) => selected.objectIds.includes(o.id));
  const groups = useMemo(() => {
    if (!a) return [];
    const ids = new Set([...a.tracks.map((t) => t.objectId), ...selectedObjects.map((o) => o.id), ...(planned?.tracks.map((t) => t.objectId) ?? [])]);
    return scene.objects
      .filter((o) => ids.has(o.id))
      .map((o) => {
        const tracks = a.tracks.filter((t) => t.objectId === o.id);
        const plannedOnly = (planned?.tracks ?? []).filter((p) => p.objectId === o.id && !tracks.some((t) => t.channel === p.channel));
        return { object: o, tracks, plannedOnly };
      });
  }, [a, scene.objects, selectedObjects, planned]);
  const plannedFrames = (objectId: string, channel: Channel) => planned?.tracks.find((p) => p.objectId === objectId && p.channel === channel)?.frames ?? [];

  if (!a) {
    return (
      <Panel title="Timeline" headingLevel={2}>
        <div className="flex flex-wrap items-center gap-3" data-testid="timeline">
          <p className="text-sm text-zinc-400">No animation in “{scene.name}” yet. Animations keyframe object position, rotation, scale, opacity, visibility and character poses over time; the scene itself is never overwritten.</p>
          <Button variant="primary" size="sm" onClick={create} data-testid="new-animation">
            + New animation
          </Button>
        </div>
      </Panel>
    );
  }

  const total = Math.max(a.durationFrames, planned ? planned.endFrame + 1 : 0);
  const width = Math.max(total * zoom, 120) + PAD * 2;
  const allKeyFrames = [...new Set(a.tracks.flatMap((t) => t.keyframes.map((k) => k.frame)))].sort((x, y) => x - y);
  const isSelected = (r: KeyRef) => keys.some((k) => refKey(k) === refKey(r));
  const single = keys.length === 1 ? keys[0]! : null;
  const singleTrack = single ? a.tracks.find((t) => t.id === single.trackId) : undefined;
  const singleKey = singleTrack?.keyframes.find((k) => k.frame === single!.frame);
  const singleObject = singleTrack ? scene.objects.find((o) => o.id === singleTrack.objectId) : undefined;
  const target = selectedObjects.length === 1 ? selectedObjects[0]! : null;

  const frameAtX = (clientX: number) => {
    const lane = tracksRef.current?.querySelector('[data-lane]') as HTMLElement | null;
    const r = lane?.getBoundingClientRect();
    if (!r) return frameNow();
    return Math.min(total - 1, Math.max(0, Math.round((clientX - r.left - PAD) / zoom)));
  };

  // ---------------------------------------------------------------- timing
  const setTiming = (patch: Parameters<typeof A.setAnimationTiming>[2], dropOutside = false) => {
    // The clock keeps the playhead's time when this animation's timing changes (see
    // PlaybackClock.load), so no seek is needed here.
    run(patch.fps ? 'Frame rate' : patch.loop ? 'Loop mode' : 'Duration', (p) => A.setAnimationTiming(p, a.id, patch, { dropOutside }));
  };
  const onDuration = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds <= 0) return;
    const frames = Math.max(1, Math.round(seconds * a.fps));
    const count = A.keyframesAfter(a, frames);
    if (count > 0) setShrink({ seconds, count });
    else setTiming({ seconds });
  };

  // ---------------------------------------------------------------- keyframes
  const moveSelected = (delta: number) => {
    if (keys.length === 0 || delta === 0) return;
    if (run('Move keyframes', (p) => A.moveKeyframes(p, a.id, keys, delta), `move-keys:${keys.map(refKey).join(',')}`)) {
      setKeys(keys.map((k) => ({ ...k, frame: k.frame + delta })));
    }
  };
  const deleteSelected = () => {
    if (keys.length && run('Delete keyframes', (p) => A.deleteKeyframes(p, a.id, keys))) setKeys([]);
  };
  const duplicateToPlayhead = () => {
    let created: KeyRef[] = [];
    if (
      run('Duplicate keyframes', (p) => {
        const r = A.duplicateKeyframes(p, a.id, keys, frameNow());
        created = r.refs;
        return r.project;
      })
    )
      setKeys(created);
  };
  /** Adds (or updates) a key at the playhead holding the channel's current value. */
  const keyTrack = (t: AnimationTrack) => {
    const frame = frameNow();
    return run('Add keyframe', (p) => A.setKeyframe(p, a.id, t.objectId, t.channel, frame, A.valueAt(p, a.id, t.objectId, t.channel, frame)));
  };
  const keyTransform = (objectId: string) => {
    const frame = frameNow();
    return run('Key transform', (p) => {
      let q = p;
      for (const c of ['x', 'y', 'rotation', 'scaleX', 'scaleY', 'opacity'] as Channel[]) q = A.setKeyframe(q, a.id, objectId, c, frame, A.valueAt(q, a.id, objectId, c, frame));
      return q;
    });
  };

  const onKeyPointerDown = (r: KeyRef) => (e: PointerEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const next = e.shiftKey ? (isSelected(r) ? keys.filter((k) => refKey(k) !== refKey(r)) : [...keys, r]) : isSelected(r) ? keys : [r];
    setKeys(next);
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({ pointerId: e.pointerId, startX: e.clientX, delta: 0, moved: false });
  };
  const onKeyPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    if (!drag || drag.pointerId !== e.pointerId) return;
    const delta = Math.round((e.clientX - drag.startX) / zoom);
    // Clamp the preview so the selection never leaves the animation.
    const lo = -Math.min(...keys.map((k) => k.frame));
    const hi = total - 1 - Math.max(...keys.map((k) => k.frame));
    const clamped = Math.min(hi, Math.max(lo, delta));
    if (clamped !== drag.delta || (!drag.moved && Math.abs(e.clientX - drag.startX) > 3)) setDrag({ ...drag, delta: clamped, moved: drag.moved || Math.abs(e.clientX - drag.startX) > 3 });
  };
  const onKeyPointerUp = (e: PointerEvent<HTMLButtonElement>) => {
    if (!drag || drag.pointerId !== e.pointerId) return;
    const d = drag;
    setDrag(null);
    // One drag = one change = one undo step; refused moves (collisions) change nothing.
    if (d.moved && d.delta !== 0) moveSelected(d.delta);
  };
  const onKeyKeyDown = (r: KeyRef) => (e: KeyboardEvent<HTMLButtonElement>) => {
    const step = e.shiftKey ? 10 : 1;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      e.stopPropagation();
      const sel = isSelected(r) ? keys : [r];
      if (!isSelected(r)) setKeys(sel);
      const delta = e.key === 'ArrowLeft' ? -step : step;
      if (run('Move keyframes', (p) => A.moveKeyframes(p, a.id, sel, delta), `move-keys:${sel.map(refKey).join(',')}`)) {
        const moved = sel.map((k) => ({ ...k, frame: k.frame + delta }));
        setKeys(moved);
        // Keep keyboard focus on the moved keyframe.
        requestAnimationFrame(() => (tracksRef.current?.querySelector(`[data-key="${r.trackId}@${r.frame + delta}"]`) as HTMLElement | null)?.focus());
      }
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      e.stopPropagation();
      const sel = isSelected(r) ? keys : [r];
      if (run('Delete keyframes', (p) => A.deleteKeyframes(p, a.id, sel))) setKeys([]);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      setKeys(e.shiftKey ? [...keys, r] : [r]);
      bus.clock.seek(r.frame);
    }
  };

  // ---------------------------------------------------------------- scrubbing
  const onScrubDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const f = frameAtX(e.clientX);
    scrubbing.current = f;
    bus.clock.pause();
    bus.clock.seek(f);
    if (!e.shiftKey && keys.length) setKeys([]);
  };
  const onScrubMove = (e: PointerEvent<HTMLDivElement>) => {
    if (scrubbing.current === null) return;
    const f = frameAtX(e.clientX);
    if (f !== scrubbing.current) {
      scrubbing.current = f;
      bus.clock.seek(f);
    }
  };
  const endScrub = () => {
    scrubbing.current = null;
  };
  const onTracksKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const k = e.key;
    if (k === ',' || k === '.') bus.clock.step(k === ',' ? -1 : 1);
    else if (k === 'Home') bus.clock.seek(0);
    else if (k === 'End') bus.clock.seek(total - 1);
    else if (k === ' ') bus.clock.toggle();
    else return;
    e.preventDefault();
  };

  const ticksEvery = a.fps * (zoom < 4 ? 2 : 1) * (zoom < 1.5 ? 2 : 1);
  const seconds = Math.ceil(total / a.fps);

  // ---------------------------------------------------------------- render
  return (
    <Panel title="Timeline" headingLevel={2}>
      <div className="flex flex-col gap-3" data-testid="timeline">
        {/* Animation management and timing */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-0.5">
            <label htmlFor="anim-select" className="text-[11px] text-zinc-400">
              Animation
            </label>
            <select id="anim-select" className={sel} value={a.id} onChange={(e) => (selection.selectAnimation(e.target.value), setKeys([]))} data-testid="animation-select">
              {animations.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>
          {renaming !== null ? (
            <form
              className="flex items-end gap-1"
              onSubmit={(e) => {
                e.preventDefault();
                if (run('Rename animation', (p) => A.renameAnimation(p, a.id, renaming))) setRenaming(null);
              }}
            >
              <div className="flex flex-col gap-0.5">
                <label htmlFor="anim-name" className="text-[11px] text-zinc-400">
                  Name
                </label>
                <input id="anim-name" className={`${sel} w-40`} value={renaming} maxLength={60} onChange={(e) => setRenaming(e.target.value)} />
              </div>
              <Button size="sm" type="submit">
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRenaming(null)}>
                Cancel
              </Button>
            </form>
          ) : (
            <Button size="sm" onClick={() => setRenaming(a.name)}>
              Rename
            </Button>
          )}
          <Button size="sm" onClick={create} data-testid="new-animation">
            + New
          </Button>
          <Button
            size="sm"
            onClick={() => {
              let id = '';
              if (
                run('Duplicate animation', (p) => {
                  const r = A.duplicateAnimation(p, a.id);
                  id = r.id;
                  return r.project;
                })
              )
                selection.selectAnimation(id);
            }}
          >
            Duplicate
          </Button>
          <Button size="sm" variant="danger" onClick={() => setDeleting(true)}>
            Delete
          </Button>
          <div className="flex flex-col gap-0.5">
            <label htmlFor="anim-duration" className="text-[11px] text-zinc-400">
              Duration (s)
            </label>
            <input
              id="anim-duration"
              key={`${a.id}:${a.durationFrames}:${a.fps}`}
              type="number"
              min={0.1}
              max={120}
              step={0.5}
              defaultValue={Math.round(durationSeconds(a) * 100) / 100}
              className={num}
              onBlur={(e) => Number(e.target.value) !== durationSeconds(a) && onDuration(Number(e.target.value))}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
              data-testid="animation-duration"
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <label htmlFor="anim-fps" className="text-[11px] text-zinc-400">
              FPS
            </label>
            <select id="anim-fps" className={sel} value={a.fps} onChange={(e) => setTiming({ fps: Number(e.target.value) })} data-testid="animation-fps">
              {ANIMATION_FPS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-0.5">
            <label htmlFor="anim-loop" className="text-[11px] text-zinc-400">
              Loop
            </label>
            <select id="anim-loop" className={sel} value={a.loop} onChange={(e) => setTiming({ loop: e.target.value as LoopMode })} data-testid="animation-loop">
              {(Object.keys(LOOP_LABELS) as LoopMode[]).map((l) => (
                <option key={l} value={l}>
                  {LOOP_LABELS[l]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-0.5">
            <label htmlFor="anim-speed" className="text-[11px] text-zinc-400">
              Speed
            </label>
            <select id="anim-speed" className={sel} value={speed} onChange={(e) => bus.clock.setSpeed(Number(e.target.value))}>
              {PLAYBACK_SPEEDS.map((s) => (
                <option key={s} value={s}>
                  {s}×
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Playback toolbar */}
        <div className="flex flex-wrap items-center gap-1" role="toolbar" aria-label="Playback">
          <Button size="sm" onClick={() => bus.clock.restart()} aria-label="Restart from frame 0" data-testid="anim-restart">
            ⏮
          </Button>
          <KeyNav frames={frames} keyFrames={allKeyFrames} direction={-1} />
          <Button size="sm" onClick={() => bus.clock.step(-1)} aria-label="Previous frame" data-testid="anim-step-back">
            ◀
          </Button>
          <Button size="sm" variant="primary" onClick={() => bus.clock.toggle()} aria-pressed={playing} aria-label={playing ? 'Pause' : 'Play'} data-testid="anim-play">
            {playing ? '❚❚ Pause' : '▶ Play'}
          </Button>
          <Button size="sm" onClick={() => bus.clock.step(1)} aria-label="Next frame" data-testid="anim-step-forward">
            ▶
          </Button>
          <KeyNav frames={frames} keyFrames={allKeyFrames} direction={1} />
          <PlayheadReadout frames={frames} animation={a} />
          <PlayheadInput frames={frames} last={total - 1} />
          <Button
            size="sm"
            variant={autoKey ? 'danger' : 'secondary'}
            onClick={() => bus.setAutoKey(!autoKey)}
            aria-pressed={autoKey}
            title="When on, moving, resizing, rotating or posing an object at the playhead records keyframes instead of changing the object itself."
            data-testid="auto-key"
          >
            <span aria-hidden="true" className={`inline-block size-2 rounded-full ${autoKey ? 'bg-white' : 'bg-zinc-500'}`} />
            Auto-Key {autoKey ? 'on' : 'off'}
          </Button>
        </div>
        {autoKey && (
          <p className="rounded-md border border-red-900 bg-red-950/40 px-2 py-1 text-xs text-red-200" role="status">
            Auto-Key is on: transform and pose edits on the canvas, the inspector and the pose editor record keyframes in “{a.name}” at the playhead (frame <PlayheadFrame frames={frames} />).
          </p>
        )}

        {/* Tracks */}
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <label htmlFor="timeline-zoom">Zoom</label>
          <input id="timeline-zoom" type="range" min={1} max={40} step={1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-32" data-testid="timeline-zoom" />
          <span className="font-mono">{zoom}px/frame</span>
          {target ? (
            <span className="ml-auto flex flex-wrap items-center gap-1">
              <AddTrack animation={a} object={target} onAdd={(c) => run('Add track', (p) => A.addTrack(p, a.id, target.id, c).project)} />
              <Button size="sm" onClick={() => keyTransform(target.id)} data-testid="key-transform">
                ◆ Key transform
              </Button>
            </span>
          ) : (
            <span className="ml-auto">Select an object to add tracks or apply a preset.</span>
          )}
        </div>
        {planned && (
          <p className="flex flex-wrap items-center gap-3 text-[11px] text-zinc-400" data-testid="timeline-legend">
            <span className="flex items-center gap-1">
              <span aria-hidden="true" className="inline-block size-2.5 rotate-45 border border-amber-200 bg-amber-400" /> existing keyframe
            </span>
            <span className="flex items-center gap-1">
              <span aria-hidden="true" className="inline-block size-2.5 rotate-45 border-2 border-sky-300" /> planned (Motion Composer preview, not applied)
            </span>
            <span className="flex items-center gap-1">
              <span aria-hidden="true" className="inline-block size-2.5 rotate-45 border border-red-200 bg-red-500" /> existing key in the plan's range (conflict)
            </span>
          </p>
        )}
        {/* The track area is a keyboard-focusable widget (playhead keys); keyframes are buttons. */}
        {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <div
          ref={tracksRef}
          className="overflow-x-auto rounded-md border border-zinc-800 bg-zinc-950"
          role="group"
          aria-label={`Tracks of “${a.name}”. Comma and period step frames, Home and End jump, Space plays or pauses.`}
          tabIndex={0}
          onKeyDown={onTracksKeyDown}
          data-testid="timeline-tracks"
        >
          <div style={{ width: LABEL_W + width + 16 }} className="relative">
            {/* Ruler (scrub here) */}
            <div className="flex border-b border-zinc-800">
              <div style={{ width: LABEL_W }} className="sticky left-0 z-20 shrink-0 bg-zinc-950 px-2 py-1 text-[11px] text-zinc-400">
                {a.fps} fps · {total} frames
              </div>
              <div
                data-lane
                className="relative h-7 cursor-ew-resize touch-none select-none"
                style={{ width }}
                onPointerDown={onScrubDown}
                onPointerMove={onScrubMove}
                onPointerUp={endScrub}
                onPointerCancel={endScrub}
                data-testid="timeline-ruler"
              >
                {Array.from({ length: Math.floor(seconds / (ticksEvery / a.fps)) + 1 }, (_, i) => i * ticksEvery).map((f) => (
                  <span key={f} className="absolute top-0 h-full border-l border-zinc-700 pl-1 text-[10px] text-zinc-400" style={{ left: PAD + f * zoom }}>
                    {Math.round(f / a.fps)}s
                  </span>
                ))}
              </div>
            </div>
            {groups.length === 0 && <p className="px-2 py-3 text-xs text-zinc-400">No tracks yet. Select an object, then add a track, key its transform, turn on Auto-Key and edit it, or apply a preset.</p>}
            {groups.map(({ object, tracks, plannedOnly }) => (
              <div key={object.id} className="border-b border-zinc-900" role="group" aria-label={`Tracks of ${object.name}`} data-testid="track-group">
                <div className="flex items-center">
                  <button
                    type="button"
                    style={{ width: LABEL_W }}
                    className={`sticky left-0 z-20 shrink-0 truncate bg-zinc-950 px-2 py-1 text-left text-xs font-semibold ${selected.objectIds.includes(object.id) ? 'text-sky-300' : 'text-zinc-200'}`}
                    onClick={() => selection.selectObjects([object.id])}
                    title={`Select ${object.name}`}
                  >
                    {object.name}
                  </button>
                  <div style={{ width }} className="h-5" />
                </div>
                {tracks.length === 0 && plannedOnly.length === 0 && <p className="sticky left-0 px-2 pb-1 text-[11px] text-zinc-400">No tracks for this object yet.</p>}
                {tracks.map((t) => (
                  <div key={t.id} className="flex items-center" data-testid="track" data-channel={t.channel}>
                    <div style={{ width: LABEL_W }} className="sticky left-0 z-20 flex shrink-0 items-center gap-1 bg-zinc-950 px-2 text-[11px] text-zinc-300">
                      <span className="flex-1 truncate">{A.CHANNEL_LABELS[t.channel]}</span>
                      <button type="button" className="rounded px-1 text-amber-300 hover:bg-zinc-800" onClick={() => keyTrack(t)} aria-label={`Add ${A.CHANNEL_LABELS[t.channel]} keyframe at the playhead for ${object.name}`} title="Key at playhead">
                        ◆+
                      </button>
                      <button type="button" className="rounded px-1 text-red-300 hover:bg-zinc-800" onClick={() => run('Remove track', (p) => A.removeTrack(p, a.id, t.id))} aria-label={`Remove ${A.CHANNEL_LABELS[t.channel]} track of ${object.name}`}>
                        ×
                      </button>
                    </div>
                    <div className="relative h-7 shrink-0 bg-zinc-900/40" style={{ width }} onPointerDown={onScrubDown} onPointerMove={onScrubMove} onPointerUp={endScrub}>
                      {t.keyframes.map((k, i) => {
                        const r = { trackId: t.id, frame: k.frame };
                        const on = isSelected(r);
                        const shift = on && drag?.moved ? drag.delta : 0;
                        const next = t.keyframes[i + 1];
                        return (
                          <span key={k.frame}>
                            {next && k.easing !== 'step' && (
                              <span aria-hidden="true" className="absolute top-1/2 h-px bg-zinc-600" style={{ left: PAD + k.frame * zoom, width: (next.frame - k.frame) * zoom }} />
                            )}
                            <button
                              type="button"
                              data-key={`${t.id}@${k.frame}`}
                              data-testid="keyframe"
                              data-frame={k.frame}
                              aria-pressed={on}
                              aria-label={`${object.name} ${A.CHANNEL_LABELS[t.channel]} keyframe at frame ${k.frame}: ${Math.round(k.value * 100) / 100}, ${EASING_LABELS[k.easing]}`}
                              data-conflict={planned?.conflicts.has(`${t.objectId}/${t.channel}@${k.frame}`) ? 'true' : undefined}
                              className={`absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rotate-45 touch-none border focus-visible:ring-2 focus-visible:ring-sky-300 ${on ? 'z-10 border-white bg-sky-400' : planned?.conflicts.has(`${t.objectId}/${t.channel}@${k.frame}`) ? 'border-red-200 bg-red-500' : 'border-amber-200 bg-amber-400'}`}
                              style={{ left: PAD + (k.frame + shift) * zoom }}
                              onPointerDown={onKeyPointerDown(r)}
                              onPointerMove={onKeyPointerMove}
                              onPointerUp={onKeyPointerUp}
                              onKeyDown={onKeyKeyDown(r)}
                            />
                          </span>
                        );
                      })}
                      <PlannedKeys frames={plannedFrames(t.objectId, t.channel)} zoom={zoom} label={`${object.name} ${A.CHANNEL_LABELS[t.channel]}`} />
                    </div>
                  </div>
                ))}
                {plannedOnly.map((p) => (
                  <div key={`planned-${p.channel}`} className="flex items-center" data-testid="planned-track" data-channel={p.channel}>
                    <div style={{ width: LABEL_W }} className="sticky left-0 z-20 flex shrink-0 items-center gap-1 bg-zinc-950 px-2 text-[11px] text-sky-300 italic">
                      <span className="flex-1 truncate">{A.CHANNEL_LABELS[p.channel]} (planned)</span>
                    </div>
                    <div className="relative h-7 shrink-0 bg-sky-950/20" style={{ width }}>
                      <PlannedKeys frames={p.frames} zoom={zoom} label={`${object.name} ${A.CHANNEL_LABELS[p.channel]}`} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
            {/* Playhead and end-of-animation marker */}
            <PlayheadLine frames={frames} left={LABEL_W + PAD} zoom={zoom} />
            <div aria-hidden="true" className="pointer-events-none absolute top-0 bottom-0 border-l-2 border-dashed border-zinc-500" style={{ left: LABEL_W + PAD + (total - 1) * zoom }} title="Last frame" />
          </div>
        </div>
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}

        {/* Keyframe editor */}
        {keys.length > 0 && (
          <div className="flex flex-wrap items-end gap-2 rounded-md border border-zinc-800 bg-zinc-900/60 p-2" data-testid="keyframe-editor">
            {single && singleKey && singleTrack ? (
              <>
                <span className="text-xs text-zinc-300">
                  {singleObject?.name} · {A.CHANNEL_LABELS[singleTrack.channel]}
                </span>
                <KeyNumber
                  id="key-frame"
                  label="Frame"
                  value={singleKey.frame}
                  onCommit={(v) => {
                    const f = Math.round(v);
                    if (run('Move keyframe', (p) => A.updateKeyframe(p, a.id, single, { frame: f }))) setKeys([{ ...single, frame: f }]);
                  }}
                />
                <KeyNumber
                  id="key-value"
                  label={`Value${singleTrack.channel === 'opacity' || singleTrack.channel === 'visible' ? ' (0–1)' : singleTrack.channel.startsWith('pose.') && !/[XY]$/.test(singleTrack.channel) ? ' (°)' : ''}`}
                  value={singleKey.value}
                  step={singleTrack.channel === 'opacity' || singleTrack.channel.endsWith('X') || singleTrack.channel.endsWith('Y') || singleTrack.channel.startsWith('scale') ? 0.01 : 1}
                  onCommit={(v) => run('Keyframe value', (p) => A.updateKeyframe(p, a.id, single, { value: v }), `key-value:${refKey(single)}`)}
                />
                <div className="flex flex-col gap-0.5">
                  <label htmlFor="key-easing" className="text-[11px] text-zinc-400">
                    Interpolation
                  </label>
                  <select
                    id="key-easing"
                    className={sel}
                    value={singleKey.easing}
                    disabled={singleTrack.channel === 'visible'}
                    onChange={(e) => run('Interpolation', (p) => A.updateKeyframe(p, a.id, single, { easing: e.target.value as Easing }))}
                    data-testid="key-easing"
                  >
                    {EasingSchema.options.map((x) => (
                      <option key={x} value={x}>
                        {EASING_LABELS[x]}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
                <span className="text-xs text-zinc-300">{keys.length} keyframes selected</span>
                <div className="flex flex-col gap-0.5">
                  <label htmlFor="keys-easing" className="text-[11px] text-zinc-400">
                    Interpolation
                  </label>
                  <select
                    id="keys-easing"
                    className={sel}
                    defaultValue=""
                    onChange={(e) =>
                      e.target.value &&
                      run('Interpolation', (p) => keys.reduce((q, r) => (a.tracks.find((t) => t.id === r.trackId)?.channel === 'visible' ? q : A.updateKeyframe(q, a.id, r, { easing: e.target.value as Easing })), p))
                    }
                  >
                    <option value="" disabled>
                      (set all)
                    </option>
                    {EasingSchema.options.map((x) => (
                      <option key={x} value={x}>
                        {EASING_LABELS[x]}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <Button size="sm" onClick={() => moveSelected(-1)} aria-label="Move selected keyframes one frame earlier">
              ← 1
            </Button>
            <Button size="sm" onClick={() => moveSelected(1)} aria-label="Move selected keyframes one frame later">
              1 →
            </Button>
            <Button size="sm" onClick={duplicateToPlayhead} data-testid="duplicate-keys">
              Copy to playhead
            </Button>
            <Button size="sm" variant="danger" onClick={deleteSelected} data-testid="delete-keys">
              Delete
            </Button>
          </div>
        )}

        {/* Motion presets */}
        <div className="flex flex-col gap-1" role="group" aria-label="Motion presets">
          <p className="text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">Presets {target ? `· ${target.name}` : ''}</p>
          <div className="flex flex-wrap gap-1">
            {MOTION_PRESET_IDS.map((id: MotionPresetId) => {
              const info = MOTION_PRESETS[id];
              const disabled = !target || (info.characterOnly && target.type !== 'character') || S.isLocked(scene, target);
              return (
                <Button key={id} size="sm" disabled={disabled} title={info.description} onClick={() => target && run(`Preset: ${info.label}`, (p) => A.applyMotionPreset(p, a.id, target.id, id))} data-testid={`preset-${id}`}>
                  {info.label}
                </Button>
              );
            })}
          </div>
          <p className="text-[11px] text-zinc-400">Presets write ordinary keyframes (replacing that object's tracks for the channels they use); walk, run and idle pose characters in place — add position keys to travel.</p>
        </div>
        {message && <p className="text-xs text-zinc-400">{message}</p>}
      </div>
      <ConfirmDialog
        open={deleting}
        danger
        title={`Delete “${a.name}”?`}
        body={`Its ${a.tracks.length} track${a.tracks.length === 1 ? '' : 's'} and ${a.tracks.reduce((n, t) => n + t.keyframes.length, 0)} keyframes are removed. Scene objects are not changed. This can be undone with Undo.`}
        confirmLabel="Delete animation"
        onCancel={() => setDeleting(false)}
        onConfirm={() => {
          setDeleting(false);
          if (run('Delete animation', (p) => A.deleteAnimation(p, a.id))) selection.selectAnimation(null);
        }}
      />
      <ConfirmDialog
        open={shrink !== null}
        danger
        title="Shorten the animation?"
        body={shrink ? `${shrink.count} keyframe${shrink.count === 1 ? ' lies' : 's lie'} after ${shrink.seconds} s and will be deleted.` : ''}
        confirmLabel="Shorten and delete keyframes"
        onCancel={() => setShrink(null)}
        onConfirm={() => {
          const s = shrink!;
          setShrink(null);
          setTiming({ seconds: s.seconds }, true);
        }}
      />
    </Panel>
  );
}

function AddTrack({ animation, object, onAdd }: { animation: Animation; object: Scene['objects'][number]; onAdd: (c: Channel) => void }) {
  const channels = ([...TRANSFORM_CHANNELS, ...(object.type === 'character' ? POSE_CHANNELS : [])] as Channel[]).filter((c) => !A.findTrack(animation, object.id, c));
  return (
    <>
      <label htmlFor="add-track" className="sr-only">
        Add a track for {object.name}
      </label>
      <select id="add-track" className={sel} value="" onChange={(e) => e.target.value && onAdd(e.target.value as Channel)} disabled={channels.length === 0} data-testid="add-track">
        <option value="">+ Track…</option>
        {channels.map((c) => (
          <option key={c} value={c}>
            {A.CHANNEL_LABELS[c]}
          </option>
        ))}
      </select>
    </>
  );
}

function KeyNumber({ id, label, value, step = 1, onCommit }: { id: string; label: string; value: number; step?: number; onCommit: (v: number) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? String(Math.round(value * 1000) / 1000);
  return (
    <div className="flex flex-col gap-0.5">
      <label htmlFor={id} className="text-[11px] text-zinc-400">
        {label}
      </label>
      <input
        id={id}
        type="number"
        step={step}
        value={shown}
        className={num}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== null && draft.trim() !== '' && Number.isFinite(Number(draft))) onCommit(Number(draft));
          setDraft(null);
        }}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        data-testid={id}
      />
    </div>
  );
}

// ---------------------------------------------------------------- per-frame pieces

const useFrame = (frames: Observable<AnimationSnapshot>) => useObservableSelector(frames, (v) => v.frame);

function PlayheadFrame({ frames }: { frames: Observable<AnimationSnapshot> }) {
  return <>{useFrame(frames)}</>;
}

function PlayheadReadout({ frames, animation: a }: { frames: Observable<AnimationSnapshot>; animation: Animation }) {
  const f = useFrame(frames);
  return (
    <span className="ml-1 font-mono text-xs text-zinc-300" data-testid="playhead" data-frame={f}>
      frame {f} / {a.durationFrames - 1} · {(f / a.fps).toFixed(2)} s / {durationSeconds(a).toFixed(2)} s
    </span>
  );
}

function PlayheadInput({ frames, last }: { frames: Observable<AnimationSnapshot>; last: number }) {
  const { animation: bus } = useServices();
  const f = useFrame(frames);
  return (
    <input
      type="number"
      min={0}
      max={last}
      value={f}
      className={`${num} ml-auto`}
      aria-label="Go to frame"
      onChange={(e) => Number.isFinite(Number(e.target.value)) && bus.clock.seek(Number(e.target.value))}
      data-testid="playhead-input"
    />
  );
}

function PlayheadLine({ frames, left, zoom }: { frames: Observable<AnimationSnapshot>; left: number; zoom: number }) {
  const f = useFrame(frames);
  return <div aria-hidden="true" className="pointer-events-none absolute top-0 bottom-0 z-30 w-0.5 bg-red-500" style={{ left: left + f * zoom }} data-testid="playhead-line" />;
}

/** Previous / next keyframe buttons (enabled state depends on the playhead). */
function KeyNav({ frames, keyFrames, direction }: { frames: Observable<AnimationSnapshot>; keyFrames: number[]; direction: 1 | -1 }) {
  const { animation: bus } = useServices();
  const f = useFrame(frames);
  const target = direction < 0 ? [...keyFrames].reverse().find((k) => k < f) : keyFrames.find((k) => k > f);
  return (
    <Button
      size="sm"
      onClick={() => target !== undefined && bus.clock.seek(target)}
      disabled={target === undefined}
      aria-label={direction < 0 ? 'Previous keyframe' : 'Next keyframe'}
      data-testid={direction < 0 ? 'anim-prev-key' : 'anim-next-key'}
    >
      {direction < 0 ? '◆◀' : '▶◆'}
    </Button>
  );
}

/** Planned (not yet applied) keyframes: hollow diamonds, not interactive. */
function PlannedKeys({ frames, zoom, label }: { frames: readonly number[]; zoom: number; label: string }) {
  if (!frames.length) return null;
  return (
    <>
      {frames.map((f) => (
        <span
          key={f}
          role="img"
          aria-label={`Planned ${label} keyframe at frame ${f}`}
          data-testid="planned-key"
          data-frame={f}
          className="pointer-events-none absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rotate-45 border-2 border-sky-300 bg-sky-400/10"
          style={{ left: PAD + f * zoom }}
        />
      ))}
    </>
  );
}
